import React, {useState, useRef, useEffect} from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import Container from "connectui/component/layout/Container";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import {format} from "date-fns";

const useStyles = (theme) =>
  buildStyles({
    titleContainer: {
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      flex: 1,
      whiteSpace: "nowrap",
    },
    preview: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "inline",
      paddingLeft: theme.spacing(2),
    },
  });

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const timestampMs =
    typeof timestamp === "number" && timestamp < 10000000000
      ? timestamp * 1000
      : timestamp;

  try {
    const timezoneAbbr = new Date()
      .toLocaleTimeString("en-us", {timeZoneName: "short"})
      .split(" ")[2];

    return `${format(new Date(timestampMs), "MMM d, yyyy HH:mm:ss.SSS")} ${timezoneAbbr}`;
  } catch (error) {
    console.error("Error formatting timestamp:", error);
    return timestamp.toString();
  }
};

const createPreview = (text) => {
  if (!text) return "";
  if (text.length <= 80) return text;

  const cutPosition = text.substring(0, 80).lastIndexOf(" ");
  return cutPosition > 30
    ? text.substring(0, cutPosition) + "..."
    : text.substring(0, 80) + "...";
};

/**
 * Log item component with collapsible content
 */
const LogItem = ({log, logItemClass, isExpanded, onExpandToggle, index}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const [localExpanded, setLocalExpanded] = useState(isExpanded);
  const contentRef = useRef(null);

  useEffect(() => {
    setLocalExpanded(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    if (onExpandToggle && index !== undefined) {
      onExpandToggle(index, localExpanded);
    }
  }, [localExpanded, onExpandToggle, index]);

  const iconMap = {
    warning: ICONS["alert-triangle"],
    error: ICONS["alert-circle"],
    info: ICONS["info"],
  };

  const iconColorMap = {
    warning: theme.colors.warning600,
    error: theme.colors.error600,
    info: theme.colors.primary600,
  };

  const customContent = (
    <>
      <FlexContainer
        alignItems="center"
        gap={theme.spacing(1.5)}
        sx={{width: "100%", cursor: "pointer"}}
        onClick={() => setLocalExpanded(!localExpanded)}
      >
        {log.severity && (
          <SvgIcon
            component={iconMap[log.severity] || ICONS["info"]}
            sx={{
              width: 16,
              height: 16,
              color: iconColorMap[log.severity] || theme.colors.primary600,
            }}
          />
        )}
        <Text
          variant="textSM"
          weight="medium"
          color={
            log.severity === "error"
              ? "error600"
              : log.severity === "warning"
                ? "warning600"
                : "gray700"
          }
          className={classes.titleContainer}
        >
          {log.title || "Guest Log Entry"}
          {!localExpanded && log.message && (
            <Text
              variant="textXS"
              color="textTertiary"
              weight="regular"
              className={classes.preview}
            >
              {createPreview(log.message)}
            </Text>
          )}
        </Text>
        <Text
          variant="textXS"
          weight="medium"
          color="textSecondary"
          paddingRight={theme.spacing(2)}
        >
          {formatTimestamp(log.timestamp)}
        </Text>
        <SvgIcon
          component={
            localExpanded ? ICONS["chevron-up"] : ICONS["chevron-down"]
          }
          sx={{
            width: "10px",
            height: "10px",
            color: theme.colors.primary600,
          }}
        />
      </FlexContainer>
      <Container
        ref={contentRef}
        sx={{
          display: localExpanded ? "block" : "none",
          marginTop: theme.spacing(2),
        }}
      >
        <Text variant="textSM" sx={{whiteSpace: "break-spaces"}}>
          {log.message || "Log description"}
        </Text>
      </Container>
    </>
  );

  return (
    <SimpleCard
      customContent={customContent}
      sx={{
        padding: theme.spacing(3, 4),
        backgroundColor: logItemClass.backgroundColor,
        boxShadow: theme.shadows.xs,
        overflow: "hidden",
      }}
    />
  );
};

export default LogItem;
